import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Play, Pause, Lock } from "lucide-react";
import { getDimensions, getRelease, getActiveWhispers } from "../lib/api";
import { Reveal } from "../components/Motion";
import Whisper from "../components/Whisper";
import { usePlayer, PLAYABLE } from "../context/PlayerContext";
import { setMeta } from "../lib/seo";
import { track as trackEvent } from "../lib/analytics";

const ROUTES = { "01": "/dimension-i", "02": "/dimension-ii", "03": "/dimension-iii", "04": "/dimension-iv" };
const ROMAN = { "01": "I", "02": "II", "03": "III", "04": "IV" };

export default function DimensionPage({ number }) {
  const navigate = useNavigate();
  const [dim, setDim] = useState(null);
  const [dims, setDims] = useState([]);
  const [release, setRelease] = useState(null);
  const [whispers, setWhispers] = useState([]);
  const { playTrack, isCurrent, playing, toggle } = usePlayer();

  useEffect(() => {
    window.scrollTo(0, 0);
    setRelease(null);
    getDimensions().then((all) => {
      setDims(all);
      const d = all.find((x) => x.number === number);
      setDim(d || false);
      if (d) {
        setMeta({
          title: `${d.title} — ${d.theme} | Motion & Meaning`,
          description: d.description,
          image: d.artwork || undefined,
          path: ROUTES[number],
        });
        trackEvent("enter_dimension", { dimension: number });
        if (d.releaseId) getRelease(d.releaseId).then(setRelease).catch(() => {});
      }
    }).catch(() => setDim(false));
    getActiveWhispers().then(setWhispers).catch(() => {});
  }, [number]);

  if (dim === false)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6">
        <p className="font-serif text-4xl">This dimension does not exist.</p>
        <Link to="/" className="font-mono text-[11px] tracking-[0.2em] text-gold">← RETURN</Link>
      </div>
    );
  if (!dim) return <div className="min-h-screen" />;

  const official = whispers.filter((w) => w.dimension === number);
  const idx = dims.findIndex((d) => d.number === number);
  const next = dims[idx + 1];

  return (
    <div data-testid={`dimension-page-${number}`}>
      {/* Hero */}
      <section className="relative min-h-[92svh] flex items-center overflow-hidden signal-veil bg-[#06070a]">
        <Whisper config={dim.whisper} id={number} official={official} />
        {dim.artwork && (
          <>
            <img src={dim.artwork} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/30" />
          </>
        )}
        <div className="mm-vignette" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 py-32 w-full">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.55em] text-gold">MULTIDIMENSIONAL</p>
            <span className="block font-serif text-[8rem] sm:text-[11rem] leading-none text-white/10 select-none mt-6">{dim.number}</span>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-white -mt-10 sm:-mt-16">{dim.title}</h1>
            <p className="font-mono text-xs md:text-sm tracking-[0.45em] text-white/50 mt-6">{dim.theme}</p>
            <span className="inline-block font-mono text-[10px] tracking-[0.35em] text-gold/70 border border-white/10 px-6 py-3 mt-8" data-testid="dimension-status">
              {dim.status}
            </span>
          </Reveal>
        </div>
      </section>

      {/* Description */}
      {dim.description && (
        <section className="py-24 md:py-32 bg-[#050505]">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <Reveal>
              <p className="font-serif text-2xl md:text-4xl font-light leading-snug text-white/85 max-w-3xl">{dim.description}</p>
            </Reveal>
          </div>
        </section>
      )}

      {/* Release / tracks OR forthcoming state */}
      {release ? (
        <section className="py-20 md:py-28 bg-[#070707]" data-testid="dimension-release">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
            <Reveal className="md:col-span-4">
              <img src={release.artwork} alt={release.title} className="w-full aspect-square object-cover rounded-[14px]" />
            </Reveal>
            <div className="md:col-span-8">
              <Reveal>
                <p className="font-mono text-[10px] tracking-[0.35em] text-gold mb-3">{release.releaseType?.toUpperCase()}</p>
                <h2 className="font-serif text-4xl font-light text-white">{release.title}</h2>
              </Reveal>
              <ul className="mt-10">
                {(release.trackList || []).map((t) => {
                  const playable = PLAYABLE.includes(t.availability);
                  const cur = isCurrent(release.slug, t.trackNumber);
                  return (
                    <li key={t.trackNumber} className="flex items-center gap-4 py-4 border-t border-white/10">
                      <button
                        onClick={() => (cur ? toggle() : playTrack(t, { releaseSlug: release.slug, releaseTitle: release.title, artwork: release.artwork, dimension: number, queue: release.trackList }))}
                        aria-label={playable ? `Play ${t.title}` : `${t.title} — ${t.lockLabel}`}
                        className={`w-9 h-9 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                          cur ? "border-gold bg-gold text-black" : playable ? "border-gold/50 text-gold hover:bg-gold hover:text-black" : "border-white/15 text-white/30"}`}
                      >
                        {!playable ? <Lock size={11} /> : cur && playing ? <Pause size={12} /> : <Play size={12} className="ml-0.5" />}
                      </button>
                      <span className="font-serif text-xl text-white flex-1 truncate">{t.title}</span>
                      {t.slug && <Link to={`/track/${t.slug}`} className="font-mono text-[9px] tracking-[0.25em] text-white/35 hover:text-gold hidden sm:block">DETAILS →</Link>}
                      <span className="font-mono text-[10px] text-white/40">{t.duration}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="flex flex-wrap gap-5 mt-10">
                <button onClick={() => navigate(`/music/${release.slug}`)} data-testid="dimension-explore-album"
                  className="bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-8 py-4 hover:bg-white transition-colors">EXPLORE THE ALBUM</button>
                <button onClick={() => navigate("/store")} className="border border-white/25 text-white/80 font-mono text-[11px] tracking-[0.25em] px-8 py-4 hover:border-gold hover:text-gold transition-colors">OWN IT</button>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-24 md:py-32 bg-[#070707] text-center" data-testid="dimension-forthcoming">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.45em] text-white/40">THE SIGNAL HAS NOT YET RESOLVED.</p>
            <p className="text-white/45 max-w-md mx-auto mt-6 leading-relaxed">
              Dimension {ROMAN[number]} is forming. Be there the moment it arrives.
            </p>
            <Link to="/join" data-testid="dimension-join-signal"
              className="inline-block mt-10 border border-gold/60 text-gold font-mono text-[11px] tracking-[0.3em] px-10 py-4 hover:bg-gold hover:text-black transition-colors">
              JOIN THE SIGNAL
            </Link>
          </Reveal>
        </section>
      )}

      {/* Next dimension nav */}
      <section className="py-16 bg-[#050505] border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex flex-wrap items-center justify-between gap-6">
          <Link to="/#dimensions" className="font-mono text-[11px] tracking-[0.2em] text-white/50 hover:text-gold transition-colors">← ALL DIMENSIONS</Link>
          {next && (
            next.restricted ? (
              <Link to="/dimension-v" data-testid="dimension-next"
                className="font-mono text-[11px] tracking-[0.25em] text-red-500/70 hover:text-red-400 transition-colors">
                DIMENSION V — ACCESS RESTRICTED →
              </Link>
            ) : (
              <Link to={ROUTES[next.number]} data-testid="dimension-next"
                className="font-mono text-[11px] tracking-[0.25em] text-white/60 hover:text-gold transition-colors">
                NEXT: {next.title} →
              </Link>
            )
          )}
        </div>
      </section>
    </div>
  );
}
